import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import DistList from "../components/DistList";
import { fmtNum, statusClass } from "../lib/format";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

export default function Email() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    apiGet("/email/summary").then(setD);
  }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;
  const k = d.kpis;

  return (
    <>
      <Masthead
        module="email"
        eyebrow="Messaging Security"
        title="Email Security"
        subtitle="Gateway filtering · threats blocked & delivered · authentication · user-reported phishing · last 24h"
        meta={<div>Mailboxes protected: <strong>{fmtNum(d.mailboxes)}</strong><br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <div className="cv-panel md:col-span-1 flex flex-col items-center justify-center">
            <div className="text-[12px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>Email Security Posture</div>
            <div className="font-serif-display text-[72px] leading-none mt-2" style={{ color: "var(--cv-green)" }}>
              {d.posture_score}%
            </div>
            <div className="text-[13px] mt-2 text-center" style={{ color: "var(--cv-charcoal-soft)" }}>
              composite of filtering, auth & account protection
            </div>
          </div>

          <Kpi testid="email-blocked" label="Threats blocked (24h)" value={k.threats_blocked} sub="phishing + malware" onClick={() => nav("/drill/email_threats?disposition=Blocked")} />
          <Kpi testid="email-delivered" label="Delivered before clawback" value={k.delivered} sub="reached inbox before SOAR action" alert onClick={() => nav("/drill/email_threats?disposition=Delivered")} />
          <Kpi testid="email-compromised" label="Accounts compromised" value={k.compromised} sub="detected + remediated · 24h" alert onClick={() => nav("/drill/identities?leaked=true")} />
        </div>

        <div className="cv-section-label">Gateway Filtering Effectiveness</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <Kpi testid="email-catch" label="Catch Rate" value={`${k.catch_rate}%`} sub="malicious mail blocked" onClick={() => nav("/drill/email_threats?disposition=Blocked")} />
          <Kpi testid="email-fpr" label="False Positive Rate" value={`${k.fpr}%`} sub="legit mail quarantined" />
          <Kpi testid="email-malware" label="Malware Blocked" value={k.threats_blocked} sub="phishing + malware · 24h" onClick={() => nav("/drill/email_threats?category=Malware%20attachment")} />
          <Kpi testid="email-delivered2" label="Threats Delivered" value={k.delivered} sub="reached inbox before clawback" alert onClick={() => nav("/drill/email_threats?disposition=Delivered")} />
          <Kpi testid="email-latency" label="Mean Filter Latency" value={`${k.latency_sec} s`} sub="scan + deliver" />
          <Kpi testid="email-zero" label="Zero-Hour Catches" value={k.zero_hour} sub="sandbox-detonated new threats" onClick={() => nav("/drill/email_threats")} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-disposition" title="Mail Disposition" subtitle="From total inbound to delivered over 24h">
            <div className="flex flex-col gap-2">
              {d.disposition.map((f) => (
                <div key={f.label} className="flex items-center gap-2">
                  <div className="text-[12px] small-caps w-36" style={{ color: "var(--cv-charcoal-soft)" }}>{f.label}</div>
                  <div className="flex-1 h-3 rounded" style={{ background: "var(--cv-line)" }}>
                    <div className="h-full rounded" style={{ width: `${Math.max(2, (f.value / d.disposition[0].value) * 100)}%`, background: f.label.includes("Delivered") ? "var(--cv-green)" : "var(--cv-amber)" }} />
                  </div>
                  <div className="text-[12px] font-bold w-20 text-right tabular-nums">{fmtNum(f.value)}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel testid="panel-threat-mix" title="Blocked Threat Categories" subtitle="Share of malicious mail blocked">
            <DistList items={d.threat_mix} onItemClick={(it) => nav(`/drill/email_threats?category=${encodeURIComponent(it.label)}`)} />
          </Panel>

          <Panel testid="panel-hourly" title="Threats Blocked vs Delivered Over Time" subtitle="Hourly — delivered line should stay near zero">
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.hourly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7DCCD" />
                  <XAxis dataKey="h" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="blocked" stroke="#A5300F" dot={false} />
                  <Line type="monotone" dataKey="delivered" stroke="#E5C243" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <div className="cv-section-label">Email Authentication</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-auth" title="Authentication Pass Rates" subtitle="SPF · DKIM · DMARC — inbound mail passing each check">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="cv-tile cv-tile-green"><div className="cv-tile-label">SPF</div><div className="cv-tile-val">{d.auth.spf}%</div></div>
              <div className="cv-tile cv-tile-green"><div className="cv-tile-label">DKIM</div><div className="cv-tile-val">{d.auth.dkim}%</div></div>
              <div className="cv-tile cv-tile-green"><div className="cv-tile-label">DMARC</div><div className="cv-tile-val">{d.auth.dmarc}%</div></div>
              <div className="cv-tile cv-tile-red"><div className="cv-tile-label">DMARC policy</div><div className="cv-tile-val">{d.auth.dmarc_policy}</div></div>
            </div>
          </Panel>

          <Panel testid="panel-dmarc-src" title="DMARC Sending Sources" subtitle="Unaligned senders are spoofing risk or misconfigured relays">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Sending Source</th>
                  <th className="text-right px-2 py-2 small-caps">Volume</th>
                  <th className="text-left px-2 py-2 small-caps">SPF</th>
                  <th className="text-left px-2 py-2 small-caps">DKIM</th>
                  <th className="text-left px-2 py-2 small-caps">Alignment</th>
                </tr>
              </thead>
              <tbody>
                {d.dmarc_sources.map((s) => (
                  <tr key={s.id} className="cv-row-clickable">
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{s.source}</strong></td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(s.volume)}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                      <span className={`status-pill ${s.spf === "pass" ? "status-compliant" : "status-non-compliant"}`}>{s.spf}</span>
                    </td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                      <span className={`status-pill ${s.dkim === "pass" ? "status-compliant" : "status-non-compliant"}`}>{s.dkim}</span>
                    </td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                      <span className={`status-pill ${s.alignment === "aligned" ? "status-compliant" : "status-non-compliant"}`}>{s.alignment}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>

        <div className="cv-section-label">User-Reported Phishing</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Kpi testid="ur-1" label="User-reported emails" value={d.user_reports.reported} sub="via report button · 24h" onClick={() => nav("/drill/email_threats?reported=true")} />
          <Kpi testid="ur-2" label="Confirmed malicious" value={d.user_reports.confirmed} sub="23% of reports" alert onClick={() => nav("/drill/email_threats?disposition=Blocked")} />
          <Kpi testid="ur-3" label="Auto-clawed back" value={d.user_reports.clawed_back} sub="copies removed from inboxes" />
          <Kpi testid="ur-4" label="Mean triage time" value={`${d.user_reports.triage_min} min`} sub="report to verdict" />
        </div>

        <div className="cv-section-label">Targeted Attacks & Impersonation</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-targets" title="Most Targeted Users / Distribution Lists" subtitle="BEC against finance is the highest-impact email threat">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Target</th>
                  <th className="text-right px-2 py-2 small-caps">Attempts</th>
                  <th className="text-left px-2 py-2 small-caps">Attack Type</th>
                  <th className="text-left px-2 py-2 small-caps">Risk</th>
                </tr>
              </thead>
              <tbody>
                {d.targets.map((t) => (
                  <tr key={t.id} className="cv-row-clickable" onClick={() => nav(`/drill/email_threats?q=${encodeURIComponent(t.target)}`)}>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{t.target}</strong></td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{t.attempts}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{t.kind}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                      <span className={`status-pill ${statusClass(t.risk)}`}>{t.risk}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel testid="panel-brands" title="Top Impersonated Brands" subtitle="Blocked spoof attempts by impersonated entity">
            {d.brands.map((b) => (
              <div key={b.id} className="flex justify-between items-center py-2 border-b text-sm" style={{ borderColor: "var(--cv-line)" }}>
                <span><strong>{b.brand}</strong></span>
                <span className="tabular-nums font-bold" style={{ color: "var(--cv-crimson)" }}>{fmtNum(b.blocks)}</span>
              </div>
            ))}
          </Panel>
        </div>

        <div className="cv-section-label">Account & Data Protection</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi testid="ap-1" label="Compromised accounts" value={d.account_protection.compromised} sub="detected + remediated · 24h" alert />
          <Kpi testid="ap-2" label="Suspicious forwarding rules" value={d.account_protection.forwarding_rules} sub="auto-forward to external" alert />
          <Kpi testid="ap-3" label="Outbound DLP blocks" value={d.account_protection.dlp_blocks} sub="sensitive data stopped" />
          <Kpi testid="ap-4" label="Impossible-travel logins" value={d.account_protection.impossible_travel} sub="flagged in mail audit" alert />
        </div>
      </div>
    </>
  );
}
